import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { Task } from '@lit/task';
import { css, html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { DataColumn, DataTableProvider } from './data-table.js';

export type AuditLogLoader = (
  pageOptions: WireTypes.PageOptions,
  signal: AbortSignal,
) => Promise<WireTypes.PagedResponse<WireTypes.AuditLogEntry>>;

// The column/detail renderers are rendered inside <data-table>'s shadow DOM,
// where this component's `static styles` don't reach — so their styles are
// inlined on the elements via styleMap.
const MUTED = { color: 'var(--sl-color-neutral-500)' };
const CODE = { fontFamily: 'var(--sl-font-mono)', fontSize: 'var(--sl-font-size-small)' };
const DETAILS_DL = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gap: 'var(--sl-spacing-2x-small) var(--sl-spacing-large)',
  margin: '0',
};
const DETAILS_DT = { color: 'var(--sl-color-neutral-500)', fontWeight: 'var(--sl-font-weight-semibold)' };
const DETAILS_DD = { margin: '0', minWidth: '0' };
const DETAILS_PRE = { margin: '0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 'var(--sl-font-size-small)' };

/**
 * Paged audit-log table with expandable per-row details. The host injects
 * `loader`, so the same component serves the deployment-wide superadmin view
 * and the org-scoped per-org view. Filtering is intentionally omitted for now.
 */
@customElement('audit-log')
class AuditLog extends LitElement {
  @property({ attribute: false }) loader?: AuditLogLoader;

  private _columns: DataColumn<WireTypes.AuditLogEntry, string>[] = [
    {
      sortAxis: undefined,
      width: 'auto',
      renderHeader: () => html`<span>When</span>`,
      renderElement: (e) => html`<sl-relative-time date=${new Date(e.createdTimestampSeconds * 1000).toISOString()}></sl-relative-time>`,
    },
    {
      sortAxis: undefined,
      width: 'auto',
      renderHeader: () => html`<span>Action</span>`,
      renderElement: (e) => html`<code style=${styleMap(CODE)}>${e.action}</code>`,
    },
    {
      sortAxis: undefined,
      width: 'auto',
      renderHeader: () => html`<span>Actor</span>`,
      renderElement: (e) => e.actorId !== undefined ? html`
        <h-box style="gap: var(--sl-spacing-2x-small); align-items: center;">
          <span>${e.actorLogin} (id:${e.actorId})</span>
          ${e.actorIsSuperuser ? html`<sl-badge variant=warning pill>superuser</sl-badge>` : nothing}
        </h-box>
      ` : html`<span style=${styleMap(MUTED)}>system</span>`,
    },
    {
      sortAxis: undefined,
      width: 'auto',
      renderHeader: () => html`<span>Organization</span>`,
      renderElement: (e) => e.orgId !== undefined ? html`<span>${e.orgSlug ?? ''} (id:${e.orgId})</span>` : nothing,
    },
    {
      sortAxis: undefined,
      width: 'auto',
      renderHeader: () => html`<span>Project</span>`,
      renderElement: (e) => e.projectId !== undefined ? html`<span>${e.projectSlug ?? ''} (id:${e.projectId})</span>` : nothing,
    },
    {
      sortAxis: undefined,
      width: '1fr',
      renderHeader: () => html`<span>IP</span>`,
      renderElement: (e) => html`<span style=${styleMap(MUTED)}>${e.ip ?? ''}</span>`,
    },
  ];

  private _renderExpanded(e: WireTypes.AuditLogEntry): TemplateResult {
    const rows: TemplateResult[] = [];
    const push = (key: string, value: unknown) => {
      const rendered = (value !== null && typeof value === 'object')
        ? html`<pre style=${styleMap(DETAILS_PRE)}>${JSON.stringify(value, null, 2)}</pre>`
        : html`<span>${String(value)}</span>`;
      rows.push(html`<dt style=${styleMap(DETAILS_DT)}>${key}</dt><dd style=${styleMap(DETAILS_DD)}>${rendered}</dd>`);
    };
    for (const [key, value] of Object.entries(e.details))
      push(key, value);
    if (e.userAgent)
      push('user agent', e.userAgent);
    if (rows.length === 0)
      return html`<span style=${styleMap(MUTED)}>No additional details.</span>`;
    return html`<dl style=${styleMap(DETAILS_DL)}>${rows}</dl>`;
  }

  private _providerTask = new Task(this, {
    args: () => [this.loader] as const,
    task: ([loader]) => {
      return {
        columns: this._columns,
        renderExpandedRow: (e) => this._renderExpanded(e),
        loader: async (pageOptions, _sortOptions, signal) => {
          if (!loader)
            return { elements: [], pageNumber: pageOptions.number, pageSize: pageOptions.size, totalElements: 0, totalPages: 0 };
          return await loader(pageOptions, signal);
        },
      } satisfies DataTableProvider<WireTypes.AuditLogEntry, string>;
    },
  });

  override render() {
    return html`
      <data-table
        .provider=${this._providerTask.value}
        .pageNumber=${0}
        .pageSize=${50}
        .noDataMessage=${'No audit entries'}
      ></data-table>
    `;
  }

  static styles = css`
    :host {
      display: block;
    }
  `;
}
